import { getEffectiveTranslations } from './i18n';

function t(lang,key){return getEffectiveTranslations(lang||'en')[key]||key;}

// BeeLove Matching Engine v3
// - 10 axes, each with a configurable weight (editable in admin)
// - computeWeightedScore: per-axis alignment score + weighted global score
// - buildAxisNarrative: one sentence per axis describing the dynamic
// - buildPortraitCroisé: 3-paragraph narrative (what you bring / they bring / together)

// ── Radar axes (display order must match FriendRadar.js) ─────────────────────
const RADAR_AXES = [
  'repair','conflict','accountability','generosity','presence',
  'narrative','stability','boredom','social','stakes',
];

// ── Default weights (also used as quiz question weights) ─────────────────────
export const DEFAULT_COMPAT_WEIGHTS = {
  repair:         2.0,
  conflict:       1.8,
  accountability: 1.9,
  generosity:     1.6,
  presence:       1.5,
  narrative:      2.0,
  stability:      1.4,
  boredom:        1.2,
  social:         1.3,
  stakes:         1.4,
};

export const AXIS_META = {
  repair:         { label:'Réparation',       icon:'🔧', category:'relational' },
  conflict:       { label:'Conflit',           icon:'⚡', category:'relational' },
  accountability: { label:'Responsabilité',    icon:'🎯', category:'relational' },
  generosity:     { label:'Générosité',        icon:'🤲', category:'relational' },
  presence:       { label:'Présence',          icon:'👁', category:'relational' },
  narrative:      { label:'Conscience de soi', icon:'🧠', category:'identity'   },
  stability:      { label:'Stabilité',         icon:'⚓', category:'identity'   },
  boredom:        { label:'Aventure',          icon:'🌊', category:'identity'   },
  social:         { label:'Social',            icon:'🌐', category:'identity'   },
  stakes:         { label:'Ambition',          icon:'🚀', category:'identity'   },
};

// ── Weight persistence (localStorage) ────────────────────────────────────────
export function getCompatWeights() {
  try {
    const stored = localStorage.getItem('beelove_compat_weights');
    if (!stored) return { ...DEFAULT_COMPAT_WEIGHTS };
    return { ...DEFAULT_COMPAT_WEIGHTS, ...JSON.parse(stored) };
  } catch { return { ...DEFAULT_COMPAT_WEIGHTS }; }
}

export function saveCompatWeights(weights) {
  localStorage.setItem('beelove_compat_weights', JSON.stringify(weights));
}

// ── Weighted compatibility score ──────────────────────────────────────────────
// scoresA / scoresB: { axis: 0-10, … }  (criteriaScores / friendRatings)
// Returns { overall: 0-100, perAxis: { [ax]: { a, b, score, weight, alignment } } }
export function computeWeightedScore(scoresA, scoresB, weights) {
  const W = weights || getCompatWeights();
  let totalW = 0, totalScore = 0;
  const perAxis = {};

  for (const ax of RADAR_AXES) {
    const a = Number(scoresA?.[ax] ?? -1);
    const b = Number(scoresB?.[ax] ?? -1);
    if (a < 0 || b < 0) continue; // skip missing axes

    const dist  = Math.abs(a - b);
    const axScore = Math.max(0, Math.round(100 - (dist / 10) * 100));
    const w     = W[ax] ?? 1;
    const avg   = (a + b) / 2;

    // Alignment classification (used by narratives)
    let alignment;
    if (dist <= 1.5) {
      alignment = avg >= 6.5 ? 'both_high' : avg <= 4.0 ? 'both_low' : 'neutral';
    } else {
      alignment = a > b ? 'a_higher' : 'b_higher';
    }

    perAxis[ax] = { a, b, dist, score: axScore, weight: w, alignment };
    totalScore += axScore * w;
    totalW     += w;
  }

  const overall = totalW > 0 ? Math.round(totalScore / totalW) : null;
  return { overall, perAxis };
}

// ── Per-axis narrative phrases ─────────────────────────────────────────────────
// Templates: (myName, theirName) => string
const AXIS_PHRASES = {
  repair: {
    both_high:  (m,t) => `Vous réduisez les conflits tous les deux rapidement — une dispute ne dure pas longtemps chez vous.`,
    both_low:   (m,t) => `Ni l'un ni l'autre ne cours vers la réconciliation. Vous apprendrez ensemble à revenir l'un vers l'autre.`,
    a_higher:   (m,t) => `${m} tend la main en premier après un accrochage. ${t} a besoin d'un peu plus de temps.`,
    b_higher:   (m,t) => `${t} initie la réconciliation — cette générosité est précieuse dans une relation.`,
    neutral:    (m,t) => `Vous trouvez votre chemin vers la réconciliation à un rythme comparable.`,
  },
  conflict: {
    both_high:  (m,t) => `Vous parlez franchement quand quelque chose accroche — une tension saine, pas de non-dits.`,
    both_low:   (m,t) => `Vous préférez tous les deux laisser passer plutôt qu'affronter directement. Utile, mais à surveiller sur la durée.`,
    a_higher:   (m,t) => `${m} nomme les problèmes directement ; ${t} préfère un tempo plus doux dans les désaccords.`,
    b_higher:   (m,t) => `${t} est direct·e dans le conflit ; ${m} a tendance à laisser décanter d'abord.`,
    neutral:    (m,t) => `Vos styles de désaccord sont proches — pas de surprise dans les discussions difficiles.`,
  },
  accountability: {
    both_high:  (m,t) => `Vous reconnaissez vos torts rapidement. Les cercles vicieux n'ont pas prise sur vous deux.`,
    both_low:   (m,t) => `Aucun de vous n'est prompt à se remettre en cause — une zone de vigilance sur le long terme.`,
    a_higher:   (m,t) => `${m} assume ses erreurs plus naturellement ; ${t} est encore en train de développer ce réflexe.`,
    b_higher:   (m,t) => `${t} a cette capacité rare de se remettre en cause — une qualité qui renforce la confiance.`,
    neutral:    (m,t) => `La responsabilité est partagée de façon équitable — personne ne garde le score.`,
  },
  generosity: {
    both_high:  (m,t) => `Deux personnes qui donnent beaucoup — veillez à recevoir aussi bien que vous offrez.`,
    both_low:   (m,t) => `Vous êtes tous les deux économes en élan généreux. Chacun respecte l'espace de l'autre.`,
    a_higher:   (m,t) => `${m} donne facilement ; ${t} est plus sélectif·ve dans ses élans. Un équilibre à construire.`,
    b_higher:   (m,t) => `${t} est naturellement généreux·se — un rappel à cultiver cet élan chez ${m} aussi.`,
    neutral:    (m,t) => `La générosité circule naturellement dans les deux sens entre vous.`,
  },
  presence: {
    both_high:  (m,t) => `Vous êtes tous les deux pleinement là. Les conversations entre vous seront denses et vraies.`,
    both_low:   (m,t) => `Vous vivez partiellement dans vos têtes — une richesse intérieure, mais des moments présents à cultiver.`,
    a_higher:   (m,t) => `${m} est très présent·e dans l'instant ; ${t} a parfois la tête ailleurs.`,
    b_higher:   (m,t) => `${t} a une qualité d'attention rare. Être avec quelqu'un d'aussi ancré·e est un cadeau.`,
    neutral:    (m,t) => `Votre niveau de présence est similaire — vous vous retrouvez au même endroit dans le moment.`,
  },
  narrative: {
    both_high:  (m,t) => `Vous avez tous les deux une conscience aiguë de qui vous êtes. Les conversations iront en profondeur.`,
    both_low:   (m,t) => `L'introspection n'est pas votre terrain de jeu préféré — vous avancez par l'action plutôt que l'analyse.`,
    a_higher:   (m,t) => `${m} se connaît bien ; ${t} est encore en train de construire son récit intérieur.`,
    b_higher:   (m,t) => `${t} a une conscience de soi développée — une profondeur que ${m} pourra apprécier au quotidien.`,
    neutral:    (m,t) => `Vous avancez ensemble dans la même direction de compréhension de vous-mêmes.`,
  },
  stability: {
    both_high:  (m,t) => `Deux identités ancrées — vous savez qui vous êtes, et ça se sent dans chaque échange.`,
    both_low:   (m,t) => `Vous êtes tous les deux en mouvement, en construction. Des compagnons d'exploration plutôt qu'un roc.`,
    a_higher:   (m,t) => `${m} est ancré·e dans qui il/elle est ; ${t} est encore en train de définir les contours.`,
    b_higher:   (m,t) => `${t} rayonne d'une stabilité intérieure — une qualité que ${m} pourrait absorber par osmose.`,
    neutral:    (m,t) => `Vos fondations identitaires sont comparables — un terreau solide pour construire quelque chose.`,
  },
  boredom: {
    both_high:  (m,t) => `Vous avez tous les deux besoin de stimulation et de renouveau. L'aventure est votre langue commune.`,
    both_low:   (m,t) => `Le quotidien doux et prévisible vous convient parfaitement à tous les deux. Pas un défaut.`,
    a_higher:   (m,t) => `${m} a besoin de nouveauté constante ; ${t} trouve son bonheur dans la continuité. À calibrer ensemble.`,
    b_higher:   (m,t) => `${t} entraîne vers de nouveaux horizons — une belle dynamique si ${m} suit le mouvement.`,
    neutral:    (m,t) => `Votre rapport à la routine et à l'aventure est bien équilibré — vous pouvez vous rejoindre.`,
  },
  social: {
    both_high:  (m,t) => `Vous aimez tous les deux le monde — vos agendas sociaux vont se remplir rapidement.`,
    both_low:   (m,t) => `Vous préférez le silence à deux aux soirées à plusieurs. Une solitude choisie, partagée.`,
    a_higher:   (m,t) => `${m} est plus tourné·e vers l'extérieur que ${t} — apprenez à calibrer ensemble vos besoins.`,
    b_higher:   (m,t) => `${t} ouvre peut-être à des cercles auxquels ${m} n'aurait pas pensé seul·e.`,
    neutral:    (m,t) => `Votre énergie sociale est accordée — vous n'avez pas à négocier vos vendredis.`,
  },
  stakes: {
    both_high:  (m,t) => `Vous visez tous les deux quelque chose de grand. L'ambition comme terrain commun.`,
    both_low:   (m,t) => `Vous préférez une vie sincère à une vie spectaculaire. C'est une forme rare de sagesse partagée.`,
    a_higher:   (m,t) => `${m} porte des ambitions plus marquées que ${t} — un moteur à partager, pas à imposer.`,
    b_higher:   (m,t) => `${t} a une vision forte de là où iel va — ${m} pourrait y trouver de l'élan.`,
    neutral:    (m,t) => `Vos niveaux d'ambition sont similaires — vous courez à la même vitesse vers l'avenir.`,
  },
};

export function buildAxisNarrative(ax, a, b, myName, theirName) {
  const phrases = AXIS_PHRASES[ax];
  if (!phrases) return null;
  const dist = Math.abs(a - b);
  const avg  = (a + b) / 2;
  let key;
  if (dist <= 1.5) {
    key = avg >= 6.5 ? 'both_high' : avg <= 4.0 ? 'both_low' : 'neutral';
  } else {
    key = a > b ? 'a_higher' : 'b_higher';
  }
  return phrases[key]?.(myName, theirName) ?? null;
}

// ── Portrait croisé ────────────────────────────────────────────────────────────
// Trait labels for high vs low scores
const TRAIT_HIGH = {
  repair:         'une capacité à renouer rapidement',
  conflict:       'une franchise dans les désaccords',
  accountability: 'une honnêteté sincère avec toi-même',
  generosity:     'une générosité naturelle',
  presence:       'une présence pleine dans l\'instant',
  narrative:      'une conscience de soi claire',
  stability:      'un ancrage identitaire fort',
  boredom:        'un appétit pour la nouveauté',
  social:         'une aisance naturelle dans le monde',
  stakes:         'des ambitions marquées',
};
const TRAIT_LOW = {
  repair:         'de la patience dans la réconciliation',
  conflict:       'une préférence pour l\'apaisement',
  accountability: 'une douceur envers tes propres erreurs',
  generosity:     'une économie de l\'élan — tu donnes quand ça compte',
  presence:       'une vie intérieure riche',
  narrative:      'une identité encore en mouvement',
  stability:      'une ouverture à te redéfinir',
  boredom:        'un goût pour la profondeur du quotidien',
  social:         'une préférence pour Scout choisis',
  stakes:         'un rapport apaisé à la réussite',
};

const SYNERGY_OUTCOME = {
  repair:         'un couple qui ne laisse pas traîner les blessures',
  conflict:       'des désaccords qui renforcent plutôt qu\'ils ne fracturent',
  accountability: 'une relation sans cercles vicieux',
  generosity:     'un élan mutuel — attention à vous laisser recharger aussi',
  presence:       'des échanges denses et vrais, sans distraction',
  narrative:      'des conversations profondes, une compréhension mutuelle rare',
  stability:      'une base solide sur laquelle construire ensemble',
  boredom:        'une vie commune pleine de surprises et d\'exploration',
  social:         'un monde commun riche, des soirées mémorables',
  stakes:         'une vision partagée — vous avancez dans la même direction',
};

const COMPLEMENT_OUTCOME = {
  repair:         'l\'un apporte la réconciliation, l\'autre la distance nécessaire',
  conflict:       'l\'un nomme, l\'autre tempère — un équilibre rare',
  accountability: 'l\'un guide l\'autre vers plus de remise en cause',
  generosity:     'l\'un inspire l\'autre à s\'ouvrir davantage',
  presence:       'l\'un ancre l\'autre dans le moment présent',
  narrative:      'l\'un enrichit l\'autre de sa profondeur intérieure',
  stability:      'l\'un ancre, l\'autre élargit — un axe dynamique',
  boredom:        'l\'un entraîne, l\'autre enracine — une tension créative',
  social:         'l\'un ouvre le monde, l\'autre préserve l\'intimité',
  stakes:         'l\'un élève le regard, l\'autre recentre sur l\'essentiel',
};

function topAxes(scores, n = 3) {
  return RADAR_AXES
    .map(ax => ({ ax, v: Number(scores?.[ax] ?? 0) }))
    .sort((a, b) => b.v - a.v)
    .slice(0, n);
}

export function buildPortraitCroisé(myScores, theirScores, myName, theirName, perAxis) {
  // 1 — What I bring (my top 3 axes)
  const myTop = topAxes(myScores, 3);
  const myTraits = myTop.map(({ ax, v }) =>
    v >= 6 ? TRAIT_HIGH[ax] : TRAIT_LOW[ax]
  ).filter(Boolean);

  // 2 — What they bring (their top 3 axes)
  const theirTop = topAxes(theirScores, 3);
  const theirTraits = theirTop.map(({ ax, v }) =>
    v >= 6 ? TRAIT_HIGH[ax] : TRAIT_LOW[ax]
  ).filter(Boolean);

  // 3 — Together: synergies (both ≥ 6.5) and complementarities (dist > 2.5)
  const synergies     = [];
  const complements   = [];
  for (const ax of RADAR_AXES) {
    const entry = perAxis?.[ax];
    if (!entry) continue;
    if (entry.alignment === 'both_high' && entry.a >= 6.5) {
      synergies.push(SYNERGY_OUTCOME[ax]);
    } else if (entry.dist > 2.5) {
      complements.push(COMPLEMENT_OUTCOME[ax]);
    }
  }

  // Paragraph 1 — Me
  const p1 = myTraits.length >= 2
    ? `Tu apportes ${myTraits.slice(0,-1).join(', ')} et ${myTraits.slice(-1)[0]}. C'est ce que ${theirName} recevra de toi — pas comme une performance, mais comme une façon d'être.`
    : myTraits.length === 1
      ? `Tu apportes avant tout ${myTraits[0]}. C'est ce que ${theirName} découvrira avec le temps.`
      : `Tes forces se révèlent dans les moments de confiance — ${theirName} les verra quand le lien sera établi.`;

  // Paragraph 2 — Them
  const p2 = theirTraits.length >= 2
    ? `${theirName} apporte ${theirTraits.slice(0,-1).join(', ')} et ${theirTraits.slice(-1)[0]}. Des qualités complémentaires aux tiennes, qui dessinent la dynamique possible.`
    : theirTraits.length === 1
      ? `${theirName} apporte avant tout ${theirTraits[0]} — une qualité qui enrichira votre quotidien.`
      : `${theirName} a des qualités qui se révèlent dans la durée — laisse le temps faire son travail.`;

  // Paragraph 3 — Together
  const togetherItems = [...synergies.slice(0, 2), ...complements.slice(0, 2)];
  let p3;
  if (togetherItems.length >= 2) {
    p3 = `Ensemble, vous créez ${togetherItems.slice(0,-1).join(', ')} et ${togetherItems.slice(-1)[0]}. Ce n'est pas une garantie — c'est un territoire possible si vous choisissez de l'habiter.`;
  } else if (togetherItems.length === 1) {
    p3 = `Ensemble, vous avez le potentiel de créer ${togetherItems[0]}. Ce que vous ferez de cette complémentarité dépend de vous.`;
  } else {
    p3 = `Vos profils sont distincts — ce n'est pas un obstacle. Les meilleures relations ne naissent pas de la ressemblance, mais de la volonté de se comprendre.`;
  }

  return { p1, p2, p3 };
}

// ── Radar area IoU (unchanged) ────────────────────────────────────────────────
export function computeRadarCompat(scoresA, scoresB) {
  const n = RADAR_AXES.length;
  const a = RADAR_AXES.map(k => Number(scoresA?.[k] ?? 0));
  const b = RADAR_AXES.map(k => Number(scoresB?.[k] ?? 0));
  if (!a.some(v => v > 0) || !b.some(v => v > 0)) return null;
  let areaA = 0, areaB = 0, areaInt = 0;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    areaA   += a[i] * a[j];
    areaB   += b[i] * b[j];
    areaInt += Math.min(a[i], b[i]) * Math.min(a[j], b[j]);
  }
  const union = areaA + areaB - areaInt;
  if (union <= 0) return null;
  return Math.round((areaInt / union) * 100);
}

// ── Quiz questions ────────────────────────────────────────────────────────────
export function getQuizQuestions(lang) { return [
  {
    id: 'repair', category: 'relational', icon: 'repair', weight: DEFAULT_COMPAT_WEIGHTS.repair,
    question: t(lang,'quiz.repair'),
    options: [
      { value: 1, label: t(lang,'quiz.repair_o1') },
      { value: 2, label: t(lang,'quiz.repair_o2') },
      { value: 3, label: t(lang,'quiz.repair_o3') },
      { value: 4, label: t(lang,'quiz.repair_o4') },
    ],
  },
  {
    id: 'conflict', category: 'relational', icon: 'conflict', weight: DEFAULT_COMPAT_WEIGHTS.conflict,
    question: t(lang,'quiz.conflict'),
    options: [
      { value: 1, label: t(lang,'quiz.conflict_o1') },
      { value: 2, label: t(lang,'quiz.conflict_o2') },
      { value: 3, label: t(lang,'quiz.conflict_o3') },
      { value: 4, label: t(lang,'quiz.conflict_o4') },
    ],
  },
  {
    id: 'accountability', category: 'relational', icon: 'accountability', weight: DEFAULT_COMPAT_WEIGHTS.accountability,
    question: t(lang,'quiz.account'),
    options: [
      { value: 1, label: t(lang,'quiz.account_o1') },
      { value: 2, label: t(lang,'quiz.account_o2') },
      { value: 3, label: t(lang,'quiz.account_o3') },
      { value: 4, label: t(lang,'quiz.account_o4') },
    ],
  },
  {
    id: 'generosity', category: 'relational', icon: 'generosity', weight: DEFAULT_COMPAT_WEIGHTS.generosity,
    question: t(lang,'quiz.generosity'),
    options: [
      { value: 1, label: t(lang,'quiz.generosity_o1') },
      { value: 2, label: t(lang,'quiz.generosity_o2') },
      { value: 3, label: t(lang,'quiz.generosity_o3') },
      { value: 4, label: t(lang,'quiz.generosity_o4') },
    ],
  },
  {
    id: 'presence', category: 'relational', icon: 'presence', weight: DEFAULT_COMPAT_WEIGHTS.presence,
    question: t(lang,'quiz.presence'),
    options: [
      { value: 1, label: t(lang,'quiz.presence_o1') },
      { value: 2, label: t(lang,'quiz.presence_o2') },
      { value: 3, label: t(lang,'quiz.presence_o3') },
      { value: 4, label: t(lang,'quiz.presence_o4') },
    ],
  },
  {
    id: 'narrative', category: 'identity', icon: 'narrative', weight: DEFAULT_COMPAT_WEIGHTS.narrative,
    question: t(lang,'quiz.narrative'),
    options: [
      { value: 1, label: t(lang,'quiz.narrative_o1') },
      { value: 2, label: t(lang,'quiz.narrative_o2') },
      { value: 3, label: t(lang,'quiz.narrative_o3') },
      { value: 4, label: t(lang,'quiz.narrative_o4') },
    ],
  },
  {
    id: 'stability', category: 'identity', icon: 'stability', weight: DEFAULT_COMPAT_WEIGHTS.stability,
    question: t(lang,'quiz.stability'),
    options: [
      { value: 1, label: t(lang,'quiz.stability_o1') },
      { value: 2, label: t(lang,'quiz.stability_o2') },
      { value: 3, label: t(lang,'quiz.stability_o3') },
      { value: 4, label: t(lang,'quiz.stability_o4') },
    ],
  },
  {
    id: 'boredom', category: 'identity', icon: 'boredom', weight: DEFAULT_COMPAT_WEIGHTS.boredom,
    question: t(lang,'quiz.boredom'),
    options: [
      { value: 1, label: t(lang,'quiz.boredom_o1') },
      { value: 2, label: t(lang,'quiz.boredom_o2') },
      { value: 3, label: t(lang,'quiz.boredom_o3') },
      { value: 4, label: t(lang,'quiz.boredom_o4') },
    ],
  },
  {
    id: 'social', category: 'identity', icon: 'social', weight: DEFAULT_COMPAT_WEIGHTS.social,
    question: t(lang,'quiz.social'),
    options: [
      { value: 1, label: t(lang,'quiz.social_o1') },
      { value: 2, label: t(lang,'quiz.social_o2') },
      { value: 3, label: t(lang,'quiz.social_o3') },
      { value: 4, label: t(lang,'quiz.social_o4') },
    ],
  },
  {
    id: 'stakes', category: 'identity', icon: 'stakes', weight: DEFAULT_COMPAT_WEIGHTS.stakes,
    question: t(lang,'quiz.stakes'),
    options: [
      { value: 1, label: t(lang,'quiz.stakes_o1') },
      { value: 2, label: t(lang,'quiz.stakes_o2') },
      { value: 3, label: t(lang,'quiz.stakes_o3') },
      { value: 4, label: t(lang,'quiz.stakes_o4') },
    ],
  },
];}

export const QUIZ_QUESTIONS = getQuizQuestions('en');

export function getCategories(lang) { return {
  identity:   { label:t(lang,'quiz.cat_identity'),   color: '#C9A84C', weight: 1.5 },
  relational: { label:t(lang,'quiz.cat_relational'), color: '#7AB8C4', weight: 1.4 },
}; }
const CATEGORIES = getCategories('en');

const COMPLEMENTARY_AXES = new Set(['social']);
const DEALBREAKER_AXES   = new Set();

function resolveAxisValue(user, axisId) {
  const quizVal   = user?.[axisId];
  const friendVal = user?.criteria_scores?.[axisId];
  if (friendVal != null) {
    const friendNorm = 1 + (friendVal / 10) * 3;
    if (quizVal != null) return { val: (quizVal + friendNorm * 1.5) / 2.5, hasFriend: true };
    return { val: friendNorm, hasFriend: true };
  }
  return { val: quizVal ?? null, hasFriend: false };
}

export function computeCompatibility(a, b) {
  if (!a || !b) return null;
  let totalWeightedScore = 0;
  let totalWeight = 0;
  const catScores = { identity: [], lifestyle: [], relational: [] };

  for (const q of QUIZ_QUESTIONS) {
    const { val: va, hasFriend: afr } = resolveAxisValue(a, q.id);
    const { val: vb, hasFriend: bfr } = resolveAxisValue(b, q.id);
    if (va == null || vb == null) continue;

    const dist = Math.abs(va - vb);
    let rawScore;
    if (DEALBREAKER_AXES.has(q.id) && dist >= 2) {
      rawScore = Math.max(0, ((3 - dist) / 3) * 100 - 20);
    } else if (COMPLEMENTARY_AXES.has(q.id)) {
      rawScore = dist <= 1 ? 90 : dist === 2 ? 65 : 35;
    } else {
      rawScore = ((3 - dist) / 3) * 100;
    }

    const friendBoost     = (afr || bfr) ? 1.4 : 1.0;
    const effectiveWeight = q.weight * friendBoost;
    catScores[q.category].push({ score: rawScore, weight: effectiveWeight });
    const catWeight = CATEGORIES[q.category]?.weight || 1.0;
    totalWeightedScore += rawScore * effectiveWeight * catWeight;
    totalWeight        += effectiveWeight * catWeight;
  }

  if (totalWeight === 0) return null;

  const breakdown = {};
  for (const [cat, items] of Object.entries(catScores)) {
    if (items.length === 0) { breakdown[cat] = null; continue; }
    const w = items.reduce((s, i) => s + i.weight, 0);
    breakdown[cat] = Math.round(items.reduce((s, i) => s + i.score * i.weight, 0) / w);
  }

  return { score: Math.round(totalWeightedScore / totalWeight), breakdown };
}

export function compatLabel(score) {
  if (score == null) return null;
  if (score >= 90) return { text: 'Exceptional match', color: '#C9A84C' };
  if (score >= 75) return { text: 'Strong match',      color: '#9CA880' };
  if (score >= 60) return { text: 'Good potential',    color: '#7AB8C4' };
  if (score >= 45) return { text: 'Some differences',  color: '#888880' };
  return               { text: 'Very different paths', color: '#C0453A' };
}

export const CATEGORY_META = CATEGORIES;
